import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#renderRichTextJsonToHtml();
    this.querySelectorAll('table').forEach(this.#formatTable);
  }

  /**
   * Shopify Rich Text fields can sometimes come through as the underlying
   * document JSON (e.g. { "type": "root", "children": [...] }) instead of HTML.
   * When that happens, we need to serialize that JSON into HTML ourselves.
   */
  #renderRichTextJsonToHtml() {
    const raw = (this.textContent || '').trim();

    // If the content doesn't look like a RichText JSON doc, leave it alone.
    if (!raw || raw.charAt(0) !== '{' || !raw.includes('"type"')) return;

    let doc;
    try {
      // Some Shopify Liquid object dumps may use Ruby-style `=>` instead of JSON `:`.
      const normalized = raw.includes('=>') ? raw.replace(/=>/g, ':') : raw;
      doc = JSON.parse(normalized);
    } catch (e) {
      // If it isn't valid JSON, don't break rendering.
      return;
    }

    const html = this.#renderNode(doc);
    if (html && typeof html === 'string') {
      this.innerHTML = html;
    }
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * @param {any} node
   * @returns {string}
   */
  #renderNode(node) {
    if (!node) return '';

    // Root documents usually have `children`.
    if (node.type === 'root' && Array.isArray(node.children)) {
      const children = /** @type {Array<any>} */ (node.children);
      return children.map((child) => this.#renderNode(child)).join('');
    }

    // Paragraphs map to <p>.
    if (node.type === 'paragraph') {
      const children = Array.isArray(node.children) ? /** @type {Array<any>} */ (node.children) : [];
      return `<p>${children.map((child) => this.#renderNode(child)).join('')}</p>`;
    }

    // Rich text leaf nodes.
    if (node.type === 'text') {
      const value = node.value ?? '';
      // Convert newline characters to <br /> so line breaks are preserved.
      // Some RichText JSON payloads store line breaks as `\n` in the text node,
      // instead of using explicit `hard_break` nodes.
      const safe = this.#escapeHtml(value).replace(/(\r\n|\n|\r)/g, '<br />');

      // Shopify/Tiptap style flags can include `bold`, `italic`, etc.
      const bold = node.bold === true;
      const italic = node.italic === true;
      const underline = node.underline === true;

      let wrapped = safe;
      if (underline) wrapped = `<u>${wrapped}</u>`;
      if (italic) wrapped = `<em>${wrapped}</em>`;
      if (bold) wrapped = `<strong>${wrapped}</strong>`;
      return wrapped;
    }

    // Lists map to <ul>/<ol> with <li>.
    if (node.type === 'list') {
      const tag = node.listType === 'ordered' ? 'ol' : 'ul';
      const children = Array.isArray(node.children) ? /** @type {Array<any>} */ (node.children) : [];
      return `<${tag}>${children.map((child) => this.#renderNode(child)).join('')}</${tag}>`;
    }

    if (node.type === 'list-item') {
      const children = Array.isArray(node.children) ? /** @type {Array<any>} */ (node.children) : [];
      return `<li>${children.map((child) => this.#renderNode(child)).join('')}</li>`;
    }

    // Line breaks (rare, but safe to support).
    if (node.type === 'hard_break' || node.type === 'hardbreak') {
      return '<br />';
    }

    // Fallback: render children if they exist.
    if (Array.isArray(node.children)) {
      const children = /** @type {Array<any>} */ (node.children);
      return children.map((child) => this.#renderNode(child)).join('');
    }

    return '';
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
